import { Grid, HStack, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { OverflowButton } from '$/components/common/OverflowButton';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { resendInvitation } from '$/services/usecases/invitation';
import { type TeacherInvitationRequest } from '$/services/usecases/invitation/mapper/invitation';

interface Props {
  teacherInvitation: TeacherInvitationRequest;
  onEdit: (invitation: TeacherInvitationRequest) => void;
  onDelete: (invitation: TeacherInvitationRequest) => void;
}

export const TeacherInvitationCard: FC<Props> = ({
  teacherInvitation,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const { mutate: resend, isPending: isResending } = useMutation({
    mutationFn: () => resendInvitation(teacherInvitation.id),
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }
      toast(t('school.teachers.resendInvitationSuccess'), 'success', '');
    },
  });

  return (
    <Grid
      alignItems='center'
      templateColumns='minmax(0, 1.5fr) minmax(0, 0.5fr) minmax(0, 1.5fr)'
      w='full'
      px='6'
      py='4'
      border='1px solid'
      borderColor='border'
      borderRadius='md'
    >
      <Stack gap='0'>
        <Text color='lighterText' fontSize='md'>
          {teacherInvitation.email}
        </Text>
        <Text fontSize='md' fontWeight='bold'>
          {teacherInvitation.firstName} {teacherInvitation.lastName}
        </Text>
      </Stack>
      <Text>{teacherInvitation.expirationDate ?? '-'}</Text>
      <HStack justifyContent='flex-end'>
        <OverflowButton
          px='6'
          fontSize='sm'
          isLoading={isResending}
          leftIcon={<Icon icon='refresh' w='14px' />}
          onClick={() => resend()}
          variant='tertiary'
        >
          {t('school.teachers.resendInvitationButton')}
        </OverflowButton>
        <OverflowButton
          px='6'
          fontSize='sm'
          leftIcon={<Icon icon='edit_pen' w='14px' />}
          onClick={() => onEdit(teacherInvitation)}
          variant='tertiary'
          hideTextOnOverflow
        >
          {t('general.edit')}
        </OverflowButton>
        <OverflowButton
          px='6'
          fontSize='sm'
          leftIcon={<Icon icon='trash_can' w='14px' />}
          onClick={() => onDelete(teacherInvitation)}
          variant='tertiary'
          hideTextOnOverflow
        >
          {t('general.delete')}
        </OverflowButton>
      </HStack>
    </Grid>
  );
};
